import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { checkChatInTrack } from '@/store/chat/actions';
import {
  clearHasRewards,
  getTrackById,
  getTrackSectionsById,
  rescheduleUserTrack,
  trackAddToUserPlan,
} from '@/store/tracks/actions';

import { selectTracks } from '@/store/tracks/selectors';

import { Alert, Button, Flex, Layout, message, Skeleton, Tabs, Typography } from 'antd';
import CalendarModal from '@/pages/CalendarModal';
import { Content } from '@/components/Layout';
import { CloseOutlined } from '@ant-design/icons';

import { TabBarExtraContent } from './TabBarExtraContent';
import TrackContent from './TrackTabs/TrackContent/TrackContent';
import TrackDescription from './TrackTabs/TrackDescription/TrackDescription';

import useImage from '@shared/hooks/useImage';
import useIsMobile from '@shared/hooks/useIsMobile';

import Utils from '@/Utils';

import css from './TrackId.module.scss';

const { Title, Text } = Typography;

const TrackId = () => {
  const { t } = useTranslation('TrackId');
  const { id, tab } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { track, isLoading, trackStudentPlanStatus, trackSections } = useSelector(selectTracks);

  const [isMobile] = useIsMobile(769);
  const [messageApi, contextMessageApi] = message.useMessage();

  const [calendarModal, setCalendarModal] = useState({ visible: false, action: '' });

  const showCalendarModal = act => setCalendarModal({ visible: true, action: act });

  // Картинка
  const cover = useImage(track?.coverImage, 'track');

  const minTrackDate = trackSections && Utils.getMinimumAllowedDate(trackSections);

  const handleCloseTrack = () => {
    let returnPath = '';

    if (location.state?.fromCollectionTracks) {
      returnPath = location.state?.returnSearch
        ? `/collection-tracks?${location.state?.returnSearch}`
        : '/collection-tracks';
    } else {
      returnPath = '/planning?tab=tracks';
    }

    navigate(returnPath, { state: {} });
  };

  const loadData = () => {
    dispatch(getTrackById(id, false));
    dispatch(getTrackSectionsById(id));
    dispatch(checkChatInTrack(id));
  };

  useEffect(() => {
    loadData();

    return () => {
      dispatch(clearHasRewards());
    };
  }, [id]);

  const trackTabs = [
    {
      key: 'description',
      label: <span data-qa='trackDescriptionTab'>{t('description')}</span>,
      children: (
        <div className={css['Track-content']}>
          <TrackDescription />
        </div>
      ),
    },
    {
      key: 'content',
      label: <span data-qa='trackContentTab'>{t('content')}</span>,
      children: (
        <div className={css['Track-content']}>
          <TrackContent />
        </div>
      ),
    },
  ];

  const handleAddToPlan = (_, selectedMonths) => {
    if (track?.id && trackSections) {
      const startDateString = selectedMonths.toString();
      const isDeadlineTracked = Utils.checkDeadlineTrack(selectedMonths, trackSections);

      if (isDeadlineTracked) {
        const params = {
          trackId: track.id,
          startDate: startDateString,
        };

        dispatch(
          trackAddToUserPlan(
            params,
            () => {
              loadData();
              setCalendarModal({ visible: false, action: '' });
            },
            () => {
              messageApi.error(t('requestError'));
            }
          )
        );
      } else {
        messageApi.error(t('errorMinDate'));
      }
    }
  };

  const handleRescheduleTrack = (_, selectedMonths) => {
    const startDate = Array.isArray(selectedMonths) && selectedMonths.length > 0 ? selectedMonths[0] : null;

    if (startDate) {
      dispatch(
        rescheduleUserTrack(
          { trackId: Number(id), startDate },
          () => {
            loadData();
            setCalendarModal({ visible: false, action: '' });
          },
          () => messageApi.error(t('rescheduleError'))
        )
      );
    } else {
      messageApi.error(t('rescheduleError'));
    }
  };

  const renderTabBarExtra = <TabBarExtraContent showCalendarModal={showCalendarModal} isMobile={isMobile} />;

  const isAddTrack = calendarModal?.action === 'add';

  const handleTabChange = key => {
    navigate(`/track/${id}/${key}`);
  };

  return (
    <Layout>
      <Content>
        <div className={css['Track']}>
          {/* Кнопка "закрыть" */}
          <Flex align='center' justify='flex-end'>
            <Button
              className={css['Track-button']}
              icon={<CloseOutlined />}
              onClick={handleCloseTrack}
              type={'link'}
              size='large'
            ></Button>
          </Flex>

          {/* Шапка трека */}
          <header className={css['Track-header']}>
            <div className={css['Track-header-content']}>
              {!isLoading ? (
                <>
                  <Title level={2} className={css['Track-header-content-title']} data-qa='trackHeaderTitle'>
                    {track?.name}
                  </Title>
                  <Text className={css['Track-header-content-description']} data-qa='trackHeaderDescription'>
                    {track?.shortDescription}
                  </Text>
                </>
              ) : (
                <Skeleton active paragraph={{ rows: 2 }} />
              )}
            </div>
            <img className={css['Track-header-cover']} src={cover} alt='' data-qa='trackHeaderCover' />
          </header>

          {!trackStudentPlanStatus && !isLoading && (
            <Alert message={<span>{t('trackProgressAddToStudyPlan')}</span>} type='info' closable showIcon />
          )}

          {isMobile && renderTabBarExtra}

          {/* Наполнение трека */}
          <Tabs
            items={trackTabs}
            tabBarExtraContent={!isMobile && renderTabBarExtra}
            defaultActiveKey={tab}
            onChange={handleTabChange}
          />
        </div>

        {/* Модалка Добавить в план */}
        {calendarModal.visible && (
          <CalendarModal
            open={calendarModal.visible}
            onOk={isAddTrack ? handleAddToPlan : handleRescheduleTrack}
            title={isAddTrack ? t('setTrackDeadline') : t('newTrackDeadline')}
            onCancel={() => setCalendarModal({ visible: false, action: '' })}
            okText={isAddTrack ? t('addToPlanTrack') : t('rescheduleTrack')}
            selectMonthEvents
            minTrackDate={minTrackDate}
          />
        )}
        {contextMessageApi}
      </Content>
    </Layout>
  );
};

export default TrackId;
